// Toast.tsx

import clsx from "clsx";
import { useEffect, useRef } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiX,
  FiXCircle,
} from "react-icons/fi";

const icons = {
  success: <FiCheckCircle />,
  error: <FiXCircle />,
  warning: <FiAlertTriangle />,
  info: <FiInfo />,
};

const styles = {
  success: "bg-green-50 text-green-700 border-green-200",
  error: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-yellow-50 text-yellow-700 border-yellow-200",
  info: "bg-blue-50 text-blue-700 border-blue-200",
};

interface Props {
  toast: ToastItem;
  onRemove: (id: string) => void;
}

export const Toast: React.FC<Props> = ({ toast, onRemove }) => {
  const timer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (toast.duration) {
      timer.current = setTimeout(() => onRemove(toast.id), toast.duration);
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [toast, onRemove]);

  return (
    <div
      role="alert"
      onMouseEnter={() => timer.current && clearTimeout(timer.current)}
      onMouseLeave={() => {
        if (toast.duration) {
          timer.current = setTimeout(() => onRemove(toast.id), toast.duration);
        }
      }}
      className={clsx(
        "w-80 flex items-start gap-3 rounded-lg border p-4 shadow-md transition-all",
        styles[toast.type],
      )}
    >
      <div className="text-xl">{icons[toast.type]}</div>

      <p className="flex-1 text-sm font-medium">{toast.message}</p>

      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className="text-gray-400 hover:text-gray-600"
      >
        <FiX />
      </button>
    </div>
  );
};
